import React from 'react';
import { Tabs as AntdTabs } from 'antd';
import type { TabsProps } from 'antd';

type TabsItems = NonNullable<TabsProps['items']>;
type TabsItem = TabsItems[number];

interface LegacyTabPaneProps {
  tab: React.ReactNode;
  children?: React.ReactNode;
  tabKey?: React.Key;
  disabled?: boolean;
  forceRender?: boolean;
  closable?: boolean;
  closeIcon?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  destroyInactiveTabPane?: boolean;
  destroyOnHidden?: boolean;
  [key: string]: unknown;
}

type LegacyTabsComponent = React.FC<TabsProps & { children?: React.ReactNode }> & {
  TabPane: React.FC<LegacyTabPaneProps>;
};

const LegacyTabPane: React.FC<LegacyTabPaneProps> = () => null;

const LegacyTabs = (({ children, items, ...props }) => {
  const {
    destroyInactiveTabPane,
    destroyOnHidden,
    ...restProps
  } = props as TabsProps & {
    destroyInactiveTabPane?: boolean;
    destroyOnHidden?: boolean;
  };

  const normalizedProps: TabsProps = {
    ...restProps,
    destroyOnHidden:
      typeof destroyOnHidden === 'boolean'
        ? destroyOnHidden
        : destroyInactiveTabPane,
  };

  let computedItems = items;

  if (!computedItems && children) {
    const childElements = React.Children.toArray(children) as React.ReactElement<LegacyTabPaneProps>[];

    computedItems = childElements
      .map((child) => {
        if (!React.isValidElement<LegacyTabPaneProps>(child)) {
          return null;
        }

        const {
          tab,
          children: tabChildren,
          tabKey,
          ...paneProps
        } = child.props;

        const itemKey = child.key ?? tabKey ?? (paneProps as { key?: React.Key }).key;

        if (itemKey == null) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('LegacyTabs.TabPane requires a `key` prop.');
          }
          return null;
        }

        const {
          closable,
          closeIcon,
          disabled,
          destroyInactiveTabPane,
          destroyOnHidden,
          forceRender,
          style,
          className,
          ...rest
        } = paneProps;

        const normalizedDestroyOnHidden =
          typeof destroyOnHidden === 'boolean'
            ? destroyOnHidden
            : destroyInactiveTabPane;

        if (process.env.NODE_ENV !== 'production' && typeof destroyInactiveTabPane === 'boolean') {
          console.warn('LegacyTabs.TabPane `destroyInactiveTabPane` is deprecated. Use `destroyOnHidden` instead.');
        }

        return {
          key: String(itemKey),
          label: tab,
          children: tabChildren,
          closable,
          closeIcon,
          disabled,
          destroyOnHidden: normalizedDestroyOnHidden,
          forceRender,
          style,
          className,
          ...rest,
        } as TabsItem;
      })
      .filter(Boolean) as TabsItem[];
  }

  return <AntdTabs {...normalizedProps} items={computedItems} />;
}) as LegacyTabsComponent;

LegacyTabs.TabPane = LegacyTabPane;

export type { LegacyTabPaneProps };
export { LegacyTabs as Tabs };
export default LegacyTabs;
